import { useEffect, useMemo } from 'react';
import { ProductionDeclarationStatusCode } from '@/types/core/purchase-order/production-declaration-status-code';

type UseProductionDeclarationGuidelineFlowParams = {
  productionDeclarationId: string;
  token?: string;
  statusCode: string;
};

type GuidelineRedirectTo = '/production-declarations/$id/submission';

export function getProductionDeclarationGuidelineVisitKey(
  productionDeclarationId: string,
  token?: string,
) {
  return [
    'production-declaration-guideline-visited',
    productionDeclarationId,
    token ?? 'no-token',
  ].join(':');
}

export function hasVisitedProductionDeclarationGuideline(
  productionDeclarationId: string,
  token?: string,
) {
  if (typeof window === 'undefined') {
    return false;
  }

  return (
    window.localStorage.getItem(
      getProductionDeclarationGuidelineVisitKey(productionDeclarationId, token),
    ) === '1'
  );
}

export function useProductionDeclarationGuidelineFlow({
  productionDeclarationId,
  token,
  statusCode,
}: UseProductionDeclarationGuidelineFlowParams) {
  const guidelineVisitKey = useMemo(
    () =>
      getProductionDeclarationGuidelineVisitKey(productionDeclarationId, token),
    [productionDeclarationId, token],
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (statusCode !== ProductionDeclarationStatusCode.Pending) {
      return;
    }

    if (window.localStorage.getItem(guidelineVisitKey) === '1') {
      return;
    }

    window.localStorage.setItem(guidelineVisitKey, '1');
  }, [statusCode, guidelineVisitKey]);

  const shouldRedirectToSubmission =
    statusCode !== ProductionDeclarationStatusCode.Pending;

  const redirectTo: GuidelineRedirectTo | undefined = shouldRedirectToSubmission
    ? '/production-declarations/$id/submission'
    : undefined;

  return {
    redirectTo,
  };
}
